import { GetActiveOrderQuery } from "../../common/generated-types";

export type ActiveOrderLine = NonNullable<GetActiveOrderQuery["activeOrder"]>["lines"][number];

export interface PricingInfo {
    id?: string;
    mov?: number;
    moq?: number;
}

export interface CartProductVariant {
    isDeleted?: boolean;
    stockLevels?: { stockOnHand?: number; stockAllocated?: number }[];
    pricing?: PricingInfo;
}

export interface SupplierTotal {
    total: number;
    totalQty: number;
    mov: number;
    moq: number;
    lines?: ActiveOrderLine[];
}

export interface SupplierReadiness {
    readyTotal: number;
    readyCount: number;
    belowCount: number;
    unreadyLineIds: string[];
}

export function isSupplierReadyForCheckout(supplier: SupplierTotal): boolean {
    return supplier.total >= supplier.mov && supplier.totalQty >= supplier.moq;
}

export function getSupplierReadiness(supplierTotals: Record<string, SupplierTotal>): SupplierReadiness {
    let readyTotal = 0;
    let readyCount = 0;
    let belowCount = 0;
    const unreadyLineIds: string[] = [];

    Object.values(supplierTotals).forEach((s) => {
        if (isSupplierReadyForCheckout(s)) {
            readyTotal += s.total;
            readyCount++;
        } else {
            belowCount++;
            s.lines?.forEach((line) => unreadyLineIds.push(line.id));
        }
    });

    return { readyTotal, readyCount, belowCount, unreadyLineIds };
}

export function groupOrderLinesBySupplier(lines: ActiveOrderLine[]): Record<string, SupplierTotal> {
    return lines.reduce((acc: Record<string, SupplierTotal>, line: ActiveOrderLine) => {
        const pricing = (line.productVariant as unknown as CartProductVariant).pricing;
        const sid = pricing?.id ?? "unknown";
        if (!acc[sid]) {
            acc[sid] = {
                total: 0,
                totalQty: 0,
                mov: pricing?.mov ?? 0,
                moq: pricing?.moq ?? 0,
                lines: [],
            };
        }
        acc[sid].total += line.linePriceWithTax;
        acc[sid].totalQty += line.quantity;
        acc[sid].lines?.push(line);
        return acc;
    }, {});
}
